import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Share2, X, Award } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, Th, Td, adminInput, Stat } from "@/components/admin/ui";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const LEVELS = ["Guest", "Preferred Guest", "Family Member", "VIP", "Founding Member", "Black Card Member", "Restricted Guest"];

export default function AdminGuests() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", membership_level: "Guest" });
  const load = () => api.get("/admin/guests", { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [q]);

  const create = async () => {
    if (!f.email) { toast.error("Email required"); return; }
    try { await api.post("/admin/guests", f); toast.success("Guest added."); setF({ first_name: "", last_name: "", email: "", phone: "", membership_level: "Guest" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setLevel = async (id, level) => { try { await api.put(`/admin/guests/${id}`, { membership_level: level }); toast.success("Membership updated."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const [activity, setActivity] = useState(null);
  const openActivity = async (g) => {
    setActivity({ loading: true, name: `${g.first_name} ${g.last_name}` });
    try { const { data } = await api.get(`/admin/guests/${g.id}/referral-activity`); setActivity(data); }
    catch (e) { toast.error(apiErr(e)); setActivity(null); }
  };

  return (
    <div>
      <AdminHeading title="Guests" subtitle={`${items?.length ?? 0} guests`}>
        <Dialog><DialogTrigger asChild><button className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2" data-testid="add-guest-btn"><Plus size={15} /> Add Guest</button></DialogTrigger>
          <DialogContent className="bg-surface border-white/10"><DialogHeader><DialogTitle className="font-serif text-2xl">Add Guest</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3"><input className={adminInput} placeholder="First name" value={f.first_name} onChange={(e) => setF((p) => ({ ...p, first_name: e.target.value }))} /><input className={adminInput} placeholder="Last name" value={f.last_name} onChange={(e) => setF((p) => ({ ...p, last_name: e.target.value }))} /></div>
              <input className={adminInput} placeholder="Email" value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))} data-testid="guest-email" />
              <input className={adminInput} placeholder="Phone" value={f.phone} onChange={(e) => setF((p) => ({ ...p, phone: e.target.value }))} />
              <select className={adminInput} value={f.membership_level} onChange={(e) => setF((p) => ({ ...p, membership_level: e.target.value }))}>{LEVELS.map((l) => <option key={l}>{l}</option>)}</select>
              <button onClick={create} className="btn-gold w-full py-2 text-sm" data-testid="guest-save">Add Guest</button>
            </div>
          </DialogContent>
        </Dialog>
      </AdminHeading>
      <input className={adminInput + " mb-4 max-w-sm"} placeholder="Search guests…" value={q} onChange={(e) => setQ(e.target.value)} />
      <Panel>
        {items === null ? <Loading /> : items.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No guests.</p> : (
          <div className="overflow-x-auto"><table className="w-full min-w-[600px]">
            <thead><tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Membership</Th></tr></thead>
            <tbody>{items.map((g) => (
              <tr key={g.id} data-testid={`guest-row-${g.id}`}>
                <Td><button onClick={() => openActivity(g)} className="text-left hover:text-gold" data-testid={`guest-activity-${g.id}`}>{g.first_name} {g.last_name}</button> {g.membership_level === "Restricted Guest" && <span className="text-red-300 text-xs">· restricted</span>}</Td>
                <Td className="text-muted-foreground">{g.email}</Td><Td className="text-muted-foreground">{g.phone || "—"}</Td>
                <Td><select className="bg-black/30 border border-white/10 rounded px-2 py-1 text-xs" value={g.membership_level} onChange={(e) => setLevel(g.id, e.target.value)}>{LEVELS.map((l) => <option key={l}>{l}</option>)}</select></Td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Panel>

      {activity && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setActivity(null)}>
          <div className="glass-dark border border-gold/30 rounded-2xl max-w-2xl w-full mt-8 mb-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="member-referral-activity">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-serif text-2xl flex items-center gap-2"><Share2 size={20} className="text-gold" /> Referral Activity — {activity.name || activity.member_name}</h3>
              <button onClick={() => setActivity(null)} className="text-muted-foreground hover:text-gold"><X size={18} /></button>
            </div>
            {activity.loading ? <Loading /> : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-5">
                  <Stat label="Total" value={activity.total} accent="gold" />
                  <Stat label="Pending" value={activity.pending} accent="pool" />
                  <Stat label="Invites Sent" value={activity.invitations_sent} accent="pool" />
                  <Stat label="Accepted" value={activity.invitations_accepted} accent="gold" />
                  <Stat label="Approved" value={activity.approved} accent="pool" />
                  <Stat label="Activated" value={activity.activated} accent="gold" />
                  <Stat label="First Stays" value={activity.first_stays} accent="gold" />
                  <Stat label="Conversion" value={`${activity.conversion_rate}%`} accent="pool" />
                </div>
                <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2">Referrals</p>
                <div className="overflow-x-auto mb-5">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/10 text-left"><Th>Referred</Th><Th>Stage</Th><Th>Suggested</Th><Th>Last Activity</Th></tr></thead>
                    <tbody>
                      {(activity.referrals || []).map((r) => (
                        <tr key={r.id} className="border-b border-white/5" data-testid={`member-ref-${r.id}`}>
                          <Td>{r.referred_name}</Td>
                          <Td><span className="text-[10px] px-2 py-0.5 rounded-full border border-gold/30 text-gold bg-gold/5">{r.stage}</span></Td>
                          <Td className="text-muted-foreground">{r.suggested_experience || "—"}</Td>
                          <Td className="text-muted-foreground whitespace-nowrap">{fmtDate(r.last_activity_at)}</Td>
                        </tr>
                      ))}
                      {(!activity.referrals || activity.referrals.length === 0) && <tr><Td colSpan={4} className="text-muted-foreground text-center py-4">No referrals from this member.</Td></tr>}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Award size={12} /> Recognition history</p>
                {(activity.recognitions || []).length === 0 ? <p className="text-xs text-muted-foreground">No recognition recorded.</p> : (activity.recognitions || []).map((rr) => (
                  <div key={rr.id} className="glass rounded-lg px-3 py-2 text-sm mb-2"><span className="text-gold text-xs">{rr.recognition_type}</span> · <span className="text-[10px] text-muted-foreground">{fmtDate(rr.created_at)} by {rr.recorded_by}</span><p>{rr.note}</p></div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
